import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import { Grid, Button, TextField, CircularProgress, Fab, MenuItem, Select } from '@material-ui/core';
import SideNav from '../../components/Admin/SideNav';
import Modal from '../../components/Modal/modal';
import MUIDataTable from 'mui-datatables';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import EditIcon from '@material-ui/icons/Edit';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import axios from '../../axios-default';
import { updateRes } from '../../store/res/res.action';
import { easySnackbar } from '../../store/Noti/noti.actions';

const GroupsStyles = makeStyles((theme) => ({
    '@global': {
        body: {
            background: '#fdfdfd',
        },
    },
    root: {
        flexGrow: 1,
    },
    rootContainer: {
        padding: 16,
        margin: '8px 0px 0px 238px',
        [theme.breakpoints.between('xs', 'sm')]: {
            margin: '12px 0px 0px 0px',
            width: '100%',
        },
        width: 'calc(100% - 238px)',
    },
    scanBox: {
        padding: 16,
        margin: '8px 0px',
    },
    textField: {
        // marginLeft: "8px",
        // marginRight: "8px",
        '& label.Mui-focused': {
            color: 'black',
        },
        '& .MuiInput-underline:after': {
            borderBottomColor: 'black',
        },
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: 'grey',
            },
            '&:hover fieldset': {
                borderColor: 'black',
            },
            '&.Mui-focused fieldset': {
                borderColor: 'black',
            },
        },
    },
    dateField: {
        width: '100%',
    },
    formControl: {
        width: '100%',
    },
    dense: {
        marginTop: 16,
        marginLeft: 0,
    },
    submitBtn: {
        backgroundColor: '#061a2e !important',
        color: 'white',
        padding: '8px 24px',
        margin: '12px 0px',
    },
    linkBtn: {
        background: 'rgba(89,54,135,1)',
        color: 'white',
        padding: '4px 24px',
        fontWeight: 300,
        margin: '16px 0px',
        width: '100%',
        [theme.breakpoints.up('sm')]: {
            padding: '4px 72px',
            width: 'unset',
        },
        '&:hover': {
            background: 'rgb(77, 74, 142)',
            color: 'white',
        },
    },
}));

const Groups = () => {
    const classes = GroupsStyles();
    const res = useSelector(({ res }) => res);
    const dispatch = useDispatch();
    const history = useHistory();

    const [modalOpen, setModalOpen] = useState(false),
        [modalError, setModalError] = useState(''),
        [updatedGroup, setUpdatedGroup] = useState({}),
        [groups, setGroups] = useState([]),
        [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                setGroups(
                    Object.values(res.groups)
                        .sort((a, b) => a.group_id - b.group_id)
                        .map((g) => ({
                            groupName: g.group_name,
                            desc: g.description,
                            distrGroupName: g.distr_group?.distr_group_name,
                            id: g,
                        }))
                );
                setLoading(false);
                setUpdatedGroup({});
            } catch (err) {
                console.error(err);
                if (err.response && err.response.statusText === 'Unauthorized') {
                    localStorage.removeItem('auth-token');
                    localStorage.removeItem('expirationDate');
                    localStorage.removeItem('role');
                    history.push('/login');
                }
            }
        })();
    }, [res.groups]);

    const Columns = useMemo(
        () => [
            {
                name: 'groupName',
                label: 'Group Name',
                options: {
                    filter: true,
                    sort: true,
                },
            },
            {
                name: 'desc',
                label: 'description',
                options: {
                    filter: true,
                    sort: true,
                },
            },
            {
                name: 'distrGroupName',
                label: 'Distr Group Name',
                options: {
                    filter: true,
                    sort: true,
                },
            },
            {
                name: 'id',
                label: 'Action',
                options: {
                    filter: false,
                    sort: false,
                    customBodyRender: (value, tableMeta, updateValue) => {
                        return (
                            <div style={{ display: 'flex' }}>
                                <Fab
                                    size="small"
                                    aria-label="add"
                                    style={{
                                        backgroundColor: '#75a0de',
                                        color: 'white',
                                        margin: '0px 4px',
                                    }}
                                    onClick={() => {
                                        setUpdatedGroup(
                                            Object.assign({}, value, {
                                                distr_group_id: value.distr_group?.distr_group_id ?? null,
                                            })
                                        );
                                        setModalOpen(true);
                                    }}
                                >
                                    <EditIcon />
                                </Fab>
                                <Fab
                                    size="small"
                                    aria-label="add"
                                    style={{
                                        backgroundColor: '#da5851',
                                        color: 'white',
                                        margin: '0px 4px',
                                    }}
                                    onClick={async () => {
                                        if (window.confirm('Are you going to delete this group?')) {
                                            try {
                                                const { data } = await axios.delete(`res/group/${value.group_id}`);
                                                if (data.success) {
                                                    setLoading(true);
                                                    await dispatch(updateRes('group'));
                                                    reloadPage();
                                                } else {
                                                    console.error(data.message);
                                                }
                                            } catch (err) {
                                                console.error(err);
                                                if (err.response && err.response.statusText === 'Unauthorized') {
                                                    localStorage.removeItem('auth-token');
                                                    localStorage.removeItem('expirationDate');
                                                    localStorage.removeItem('role');
                                                    history.push('/login');
                                                }
                                            }
                                        }
                                    }}
                                >
                                    <DeleteOutlineIcon />
                                </Fab>
                            </div>
                        );
                    },
                },
            },
        ],
        []
    );

    const reloadPage = useCallback(() => {
        history.push('/blank');
        history.replace(history.location.pathname);
    }, [history]);

    const handleUpdate = useCallback(
        (field) =>
            ({ target: { value } }) => {
                setUpdatedGroup({
                    ...updatedGroup,
                    [field]: value,
                });
            },
        [updatedGroup]
    );

    const handleSave = useCallback(async () => {
        try {
            if (loading) return;
            setLoading(true);
            if (String.isBlank(updatedGroup.group_name)) {
                return setModalError('Group Name is required');
            }
            if (!updatedGroup.distr_group_id) {
                return setModalError('Distributor group is required');
            }
            await axios.post(`res/group`, updatedGroup);
            window.scroll({ top: 0, left: 0, behavior: 'smooth' });
            dispatch(easySnackbar('Success', 'success'));

            dispatch(updateRes('group'));
            reloadPage();
            setLoading(true);
            setModalOpen(false);
        } catch (err) {
            console.error(err);
            if (err.response && err.response.statusText === 'Unauthorized') {
                localStorage.removeItem('auth-token');
                localStorage.removeItem('expirationDate');
                localStorage.removeItem('role');
                history.push('/login');
            }
        } finally {
            setLoading(false);
        }
    }, [updatedGroup]);

    const distrgroupSelect = useMemo(
        () =>
            [
                <MenuItem value={undefined} key={-1}>
                    None
                </MenuItem>,
            ].concat(
                Object.entries(res.distr_groups).map(([id, dg]) => (
                    <MenuItem value={id} key={id}>
                        {dg.distr_group_name}
                    </MenuItem>
                ))
            ),
        [res?.distr_groups, loading]
    );

    return (
        <div>
            <SideNav />
            <Grid container className={classes.rootContainer}></Grid>
            <Grid container className={classes.rootContainer} style={{ marginTop: 0 }}>
                {!loading ? (
                    <div style={{ display: 'contents' }}>
                        <Grid container>
                            <Grid
                                item
                                xs={12}
                                style={{
                                    padding: 8,
                                }}
                            >
                                <h1
                                    style={{
                                        marginBlockEnd: 0,
                                    }}
                                >
                                    Groups
                                </h1>

                                <hr />
                            </Grid>

                            <Grid item xs={12} style={{ padding: 8 }}>
                                <Button className={classes.linkBtn} onClick={() => setModalOpen(true)}>
                                    Add Group
                                </Button>
                                <MUIDataTable
                                    title={'Group List'}
                                    data={groups}
                                    columns={Columns}
                                    options={{
                                        download: false,
                                        filterType: 'dropdown',
                                        responsive: 'scroll',
                                        selectableRows: false,
                                        rowsPerPage: 30,
                                        rowsPerPageOptions: [15, 30, 50],
                                    }}
                                />
                                <Modal
                                    className="modal"
                                    modal="Group"
                                    title="Modify Group"
                                    open={modalOpen}
                                    handleClose={() => setModalOpen(false)}
                                    handleDataSave={() => handleSave()}
                                    modalError={modalError}
                                    submitButton={true}
                                >
                                    <>
                                        <Grid item xs={12} style={{ padding: 4 }}>
                                            <TextField
                                                id="group_name"
                                                label="Group Name"
                                                margin="normal"
                                                style={{ width: '100%' }}
                                                value={updatedGroup.group_name}
                                                onChange={handleUpdate('group_name')}
                                            />
                                        </Grid>
                                        <Grid item xs={12} style={{ padding: 4 }}>
                                            <TextField
                                                id="description"
                                                label="Description"
                                                style={{ width: '100%' }}
                                                margin="normal"
                                                value={updatedGroup.description}
                                                onChange={handleUpdate('description')}
                                            />
                                        </Grid>
                                        <Grid item xs={12} style={{ padding: 4 }}>
                                            <p>Select Distr Group</p>
                                            <Select
                                                displayEmpty
                                                onChange={handleUpdate('distr_group_id')}
                                                style={{ width: '100%' }}
                                                value={updatedGroup.distr_group_id}
                                            >
                                                {distrgroupSelect}
                                            </Select>
                                        </Grid>
                                    </>
                                </Modal>
                            </Grid>
                        </Grid>
                    </div>
                ) : (
                    <Grid container style={{ display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress style={{ color: 'rgb(6,26,46)' }} />
                    </Grid>
                )}
            </Grid>
        </div>
    );
};

export default Groups;
